import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { Wordmark } from "@/components/Wordmark";

export default function Navbar({ onJoin }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky lg:fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "bg-ink/85 backdrop-blur-md border-b border-zinc-800"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-5 sm:px-10 h-16 sm:h-20">
        <a href="#top" data-testid="nav-logo" className="shrink-0">
          <Wordmark className="text-2xl sm:text-3xl" />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-300 hover:text-volt transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/sessions"
            data-testid="nav-link-sessions"
            className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-300 hover:text-volt transition-colors duration-300"
          >
            Sessions
          </a>
          <a
            href="/login"
            data-testid="nav-link-signin"
            className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-300 hover:text-volt transition-colors duration-300"
          >
            Sign in
          </a>
          <div className="flex items-center gap-2 border-l border-zinc-800 pl-4">
            <a href="/coach-login" data-testid="nav-coach-login"
              className="border border-zinc-700 px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-400 hover:border-volt hover:text-volt transition-colors">
              Coach
            </a>
            <a href="/admin-login" data-testid="nav-admin-login"
              className="border border-zinc-700 px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-400 hover:border-volt hover:text-volt transition-colors">
              Admin
            </a>
          </div>
          <a
            href="/assessment"
            data-testid="nav-assessment-cta"
            className="bg-volt text-black text-xs uppercase tracking-[0.18em] font-extrabold px-6 py-3 hover:bg-white transition-colors duration-300"
          >
            Take the Assessment
          </a>
        </div>

        <button
          data-testid="nav-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-ink/95 backdrop-blur-md border-t border-zinc-800 px-5 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setOpen(false)}
              className="font-display uppercase text-3xl tracking-wide text-zinc-200 hover:text-volt transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/assessment"
            data-testid="mobile-nav-assessment-link"
            onClick={() => setOpen(false)}
            className="font-display uppercase text-3xl tracking-wide text-volt"
          >
            Free Assessment
          </a>
          <a
            href="/sessions"
            data-testid="mobile-nav-sessions-link"
            onClick={() => setOpen(false)}
            className="font-display uppercase text-3xl tracking-wide text-zinc-200 hover:text-volt transition-colors"
          >
            Group Sessions
          </a>
          <a
            href="/login"
            data-testid="mobile-nav-signin-link"
            onClick={() => setOpen(false)}
            className="font-display uppercase text-3xl tracking-wide text-zinc-200 hover:text-volt transition-colors"
          >
            Sign In
          </a>
          <div className="grid grid-cols-2 gap-2">
            <a href="/coach-login" data-testid="mobile-nav-coach-login" onClick={() => setOpen(false)}
              className="border border-zinc-700 px-4 py-3 text-center text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-400 hover:border-volt hover:text-volt transition-colors">
              Coach Login
            </a>
            <a href="/admin-login" data-testid="mobile-nav-admin-login" onClick={() => setOpen(false)}
              className="border border-zinc-700 px-4 py-3 text-center text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-400 hover:border-volt hover:text-volt transition-colors">
              Admin Login
            </a>
          </div>
          <a
            href="/assessment"
            data-testid="mobile-nav-assessment-cta"
            onClick={() => setOpen(false)}
            className="mt-2 bg-volt text-black uppercase tracking-[0.18em] font-extrabold text-sm px-6 py-4 text-center"
          >
            Take the Assessment
          </a>
        </div>
      )}
    </header>
  );
}
